import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CalendarClock, CheckCircle2, ChevronRight, ClipboardList, Eye, FileText, Lightbulb, Megaphone, Monitor, Send, Tag, Trash2, Users, X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type Package, type Site, type Zone } from '../api/client';
import { NeedsSite } from '../app/Layout';
import { useSession, useSite } from '../app/session';
import { ask } from '../components/Confirm';
import { useAssetUrls } from '../components/EntityForm';
import { Select } from '../components/Select';
import { ErrorBox, Loading, PageHeader, Toggle } from '../components/ui';
import { fromLocalInput, toLocalInput, useRooms } from './GuestsPage';
import { ImagePicker } from './LiveTvPage';
import {
  audienceText,
  CampaignVisual,
  FORMAT_LABEL,
  FREQ_LABEL,
  messageBody,
  ROOM_KINDS,
  stateOf,
  TRIGGER_LABEL,
  TYPE_LABEL,
  type Audience,
  type Message,
} from './MarketingPage';

const NEW: Partial<Message> = {
  type: 'MARKETING',
  format: 'POPUP',
  trigger: 'IMMEDIATE',
  frequency: 'ONCE_PER_STAY',
  stopOnDismiss: true,
  audience: {},
  status: 'ACTIVE',
};

export function CampaignPage() {
  const site = useSite();
  const { id } = useParams();
  return <NeedsSite>{site ? <CampaignLoader key={`${site.id}/${id ?? 'new'}`} site={site} id={id} /> : <PageHeader title="Campaign" />}</NeedsSite>;
}

function CampaignLoader({ site, id }: { site: Site; id?: string }) {
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['messages', site.id, 'campaigns'],
    queryFn: () => api.get<{ items: Message[] }>(`/sites/${site.id}/messages?view=campaigns`),
    enabled: !!id,
  });
  if (!id) return <CampaignEditor site={site} initial={null} />;
  if (list.isLoading) return <Loading />;
  const m = list.data?.items.find((x) => x.id === id);
  if (!m)
    return (
      <div className="card empty">
        This campaign was not found.{' '}
        <button className="btn small" onClick={() => navigate('/marketing')}>
          Back to Marketing
        </button>
      </div>
    );
  return <CampaignEditor site={site} initial={m} />;
}

function Section({ icon, title, sub, children, aside }: { icon: ReactNode; title: string; sub: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="card camp-section">
      <div className="pkg-head">
        <span className="pkg-head-icon">{icon}</span>
        <span style={{ flex: 1 }}>
          <span className="pkg-head-title">{title}</span>
          <span className="muted" style={{ display: 'block', fontSize: 13 }}>
            {sub}
          </span>
        </span>
        {aside}
      </div>
      <div className="camp-grid">{children}</div>
    </div>
  );
}

function F({ label, hint, children, wide, req }: { label: string; hint?: string; children: ReactNode; wide?: boolean; req?: boolean }) {
  return (
    <label className={`camp-field${wide ? ' wide' : ''}`}>
      <span className="camp-label">
        {label}
        {req && <span style={{ color: '#dc2626' }}> *</span>}
      </span>
      {children}
      {hint && <span className="camp-hint">{hint}</span>}
    </label>
  );
}

function CampaignEditor({ site, initial }: { site: Site; initial: Message | null }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const assetUrl = useAssetUrls();
  const rooms = useRooms(site.id);
  const zones = useQuery({ queryKey: ['zones', site.id], queryFn: () => api.get<{ items: Zone[] }>(`/sites/${site.id}/zones`) });
  const packages = useQuery({ queryKey: ['packages'], queryFn: () => api.get<{ items: Package[] }>('/packages') });
  const [m, setM] = useState<Partial<Message>>(initial ?? NEW);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const set = (patch: Partial<Message>) => {
    setM((v) => ({ ...v, ...patch }));
    setDirty(true);
  };
  const aud = m.audience ?? {};
  const setAud = (patch: Partial<Audience>) => {
    const next: Audience = { ...aud, ...patch };
    for (const k of Object.keys(next) as (keyof Audience)[]) {
      const v = next[k];
      if (v === undefined || (Array.isArray(v) && v.length === 0)) delete next[k];
    }
    set({ audience: next });
  };
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const save = useMutation({
    mutationFn: (status: Message['status']) => {
      const body = { ...messageBody(m), status };
      return initial ? api.patch(`/messages/${initial.id}`, body) : api.post(`/sites/${site.id}/messages`, body);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['messages', site.id] });
      setDirty(false);
      navigate('/marketing');
    },
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/messages/${initial!.id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['messages', site.id] });
      navigate('/marketing');
    },
  });
  const leave = () => (dirty ? ask('Leave without saving? Your changes to this campaign will be lost.', () => navigate('/marketing'), { action: 'Leave', danger: true }) : navigate('/marketing'));
  const ready = !!(m.title || m.body);
  const img = imageUrl ?? assetUrl(m.imageAssetId);
  const editable = can('ADMIN');

  const multi = (key: 'roomIds' | 'zoneIds' | 'packageIds', options: { id: string; label: string }[]) => {
    const selected = aud[key] ?? [];
    const names = new Map(options.map((o) => [o.id, o.label]));
    return (
      <div className="camp-multi">
        {selected.map((id) => (
          <span key={id} className="chip" style={{ color: '#5b21b6', background: '#f1ebff', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {names.get(id) ?? '…'}
            <button type="button" className="chip-x" onClick={() => setAud({ [key]: selected.filter((x) => x !== id) })} aria-label="Remove">
              <X size={11} />
            </button>
          </span>
        ))}
        <Select value="" onChange={(e) => e.target.value && setAud({ [key]: [...selected, e.target.value] })} aria-label="Add">
          <option value="">{selected.length ? '+ Add' : 'Any'}</option>
          {options
            .filter((o) => !selected.includes(o.id))
            .map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
        </Select>
      </div>
    );
  };

  const schedule = [m.trigger && TRIGGER_LABEL[m.trigger], m.startAt && `from ${new Date(m.startAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}`].filter(Boolean).join(' ');
  const status = initial ? stateOf({ status: m.status ?? 'DRAFT', startAt: m.startAt ?? null }) : { label: 'New', cls: 'offline' };

  return (
    <>
      <div className="crumbs">
        <button type="button" onClick={leave} title="Back">
          <ArrowLeft size={15} />
        </button>
        <button type="button" onClick={leave}>
          Marketing
        </button>
        <ChevronRight size={14} />
        <strong>{initial ? initial.name || initial.title || 'Campaign' : 'New campaign'}</strong>
      </div>
      <PageHeader
        title={initial ? 'Edit campaign' : 'New campaign'}
        sub="A promotion or announcement shown on the TVs, with its audience and schedule."
        actions={
          editable && (
            <>
              {initial && (
                <button className="btn danger-outline" onClick={() => ask(`Delete ${initial.name || initial.title || 'this campaign'}? It stops showing on the TVs.`, () => remove.mutate())}>
                  <Trash2 size={16} /> Delete
                </button>
              )}
              <button className="btn" disabled={!ready || save.isPending} onClick={() => save.mutate('DRAFT')} title="Saved but not shown on the TVs">
                <FileText size={16} /> Save draft
              </button>
              <button className="btn primary" disabled={!ready || save.isPending} onClick={() => save.mutate('ACTIVE')}>
                <Send size={16} /> {initial ? (initial.status === 'ACTIVE' ? 'Save changes' : 'Save and publish') : 'Create campaign'}
              </button>
            </>
          )
        }
      />
      <ErrorBox error={save.error ?? remove.error} />

      <div className="camp-layout">
        <div className="stack">
          <Section icon={<Megaphone size={22} />} title="Content" sub="What the guests read, and how it looks on the TV.">
            <F label="Campaign name" hint="Only shown in the admin.">
              <input value={m.name ?? ''} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Spa week" />
            </F>
            <F label="Type" req>
              <Select value={m.type} onChange={(e) => set({ type: e.target.value as Message['type'] })}>
                <option value="MARKETING">Marketing promotion</option>
                <option value="PROMO">Background promotion (lowest priority)</option>
                <option value="OPERATIONAL">Operational notice (e.g. pool closed)</option>
              </Select>
            </F>
            <F label="Title" req hint="A title or a text is needed.">
              <input value={m.title ?? ''} maxLength={120} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. 20% off at the Spa" />
            </F>
            <F label="Text" hint={`${(m.body ?? '').length}/500`}>
              <textarea value={m.body ?? ''} maxLength={500} onChange={(e) => set({ body: e.target.value })} style={{ minHeight: 86 }} placeholder="e.g. Relax at the Serenity Spa. All massages and treatments are 20% off this week." />
            </F>
            <div className="camp-field">
              <ImagePicker
                label="Image"
                variant="drop"
                hint="Landscape works best for pop-up and full screen."
                value={img}
                onChange={(a) => {
                  set({ imageAssetId: a?.id ?? null });
                  setImageUrl(a?.url ?? null);
                }}
              />
            </div>
            <div className="camp-grid" style={{ padding: 0, alignContent: 'start' }}>
              <F label="Display" req>
                <Select value={m.format} onChange={(e) => set({ format: e.target.value as Message['format'] })}>
                  {Object.entries(FORMAT_LABEL).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </Select>
              </F>
              <F label="Hide after (seconds)" hint="Empty = until the guest closes it.">
                <input type="number" min={1} value={m.durationSec ?? ''} onChange={(e) => set({ durationSec: e.target.value ? Number(e.target.value) : null })} placeholder="e.g. 10" />
              </F>
            </div>
            <F label="QR code link" hint="Shown as a QR code (booking page, menu…).">
              <input type="url" value={m.qrUrl ?? ''} onChange={(e) => set({ qrUrl: e.target.value })} placeholder="https://" />
            </F>
            <F label="Button label" hint="E.g. “Book now”.">
              <input value={m.ctaLabel ?? ''} maxLength={40} onChange={(e) => set({ ctaLabel: e.target.value })} placeholder="e.g. Book now" />
            </F>
          </Section>

          <Section icon={<Users size={22} />} title="Audience" sub="Where and to whom the campaign is shown. Empty means everyone.">
            <F label="Floors / areas">{multi('zoneIds', (zones.data?.items ?? []).map((z) => ({ id: z.id, label: z.name })))}</F>
            <F label="Rooms" hint="Floors and rooms: a TV matches any of them.">
              {multi('roomIds', (rooms.data?.items ?? []).map((r) => ({ id: r.id, label: r.number })))}
            </F>
            <F label="Room kinds">
              <span className="row" style={{ gap: 6 }}>
                {ROOM_KINDS.map((k) => {
                  const on = aud.roomKinds?.includes(k) ?? false;
                  return (
                    <button
                      key={k}
                      type="button"
                      className={`pick-chip${on ? ' on' : ''}`}
                      onClick={() => setAud({ roomKinds: on ? aud.roomKinds!.filter((x) => x !== k) : [...(aud.roomKinds ?? []), k] })}
                    >
                      {k.replace('_', ' ').toLowerCase()}
                    </button>
                  );
                })}
              </span>
            </F>
            <F label="Packages">{multi('packageIds', (packages.data?.items ?? []).map((p) => ({ id: p.id, label: p.name })))}</F>
            <F label="Guest languages" hint="Comma-separated, e.g. en, ar, de. Empty = all.">
              <input value={(aud.languages ?? []).join(', ')} onChange={(e) => setAud({ languages: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })} placeholder="e.g. en, ar, de" />
            </F>
            <F label="VIP / guest segment">
              <Select value={aud.vip === undefined ? '' : String(aud.vip)} onChange={(e) => setAud({ vip: e.target.value === '' ? undefined : e.target.value === 'true' })}>
                <option value="">All guests</option>
                <option value="true">VIP only</option>
                <option value="false">Non-VIP only</option>
              </Select>
            </F>
            <div className="camp-field wide row" style={{ gap: 10 }}>
              <Toggle on={!!aud.occupiedOnly} onChange={(v) => setAud({ occupiedOnly: v || undefined })} label="Occupied rooms only" />
              <strong style={{ fontSize: 13.5 }}>Occupied rooms only</strong>
              <span className="muted" style={{ fontSize: 12.5 }}>Only on TVs in rooms with a checked-in guest.</span>
            </div>
          </Section>

          <Section icon={<CalendarClock size={22} />} title="Schedule" sub="When and how often the campaign is shown.">
            <F label="Show" req>
              <Select value={m.trigger} onChange={(e) => set({ trigger: e.target.value as Message['trigger'] })}>
                {Object.entries(TRIGGER_LABEL).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </Select>
            </F>
            {m.trigger === 'BEFORE_CHECKOUT' ? (
              <F label="Hours before check-out">
                <input type="number" value={m.triggerHours ?? 12} onChange={(e) => set({ triggerHours: Number(e.target.value) || null })} />
              </F>
            ) : (
              <span />
            )}
            <F label="Start date & time" hint="Empty = now.">
              <input type="datetime-local" value={toLocalInput(m.startAt)} onChange={(e) => set({ startAt: fromLocalInput(e.target.value) })} />
            </F>
            <F label="End date & time" hint="Empty = until paused.">
              <input type="datetime-local" value={toLocalInput(m.endAt)} onChange={(e) => set({ endAt: fromLocalInput(e.target.value) })} />
            </F>
            <F label="Daily from" hint="Hotel time. E.g. happy hour 16:00–19:00.">
              <input type="time" value={m.dailyFrom ?? ''} onChange={(e) => set({ dailyFrom: e.target.value || null })} />
            </F>
            <F label="Daily until">
              <input type="time" value={m.dailyTo ?? ''} onChange={(e) => set({ dailyTo: e.target.value || null })} />
            </F>
            <F label="Frequency" req>
              <Select value={m.frequency} onChange={(e) => set({ frequency: e.target.value as Message['frequency'] })}>
                {Object.entries(FREQ_LABEL).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </Select>
            </F>
            {m.frequency === 'EVERY_HOURS' ? (
              <F label="Every (hours)">
                <input type="number" value={m.everyHours ?? 24} onChange={(e) => set({ everyHours: Number(e.target.value) || null })} />
              </F>
            ) : (
              <F label="Max times per TV" hint="Empty = no limit.">
                <input type="number" value={m.maxImpressions ?? ''} onChange={(e) => set({ maxImpressions: e.target.value ? Number(e.target.value) : null })} placeholder="e.g. 3" />
              </F>
            )}
            <div className="camp-field wide row" style={{ gap: 10 }}>
              <Toggle on={m.stopOnDismiss ?? true} onChange={(v) => set({ stopOnDismiss: v })} label="Stop once closed" />
              <strong style={{ fontSize: 13.5 }}>Stop showing once the guest closes it</strong>
            </div>
          </Section>
        </div>

        <div className="stack camp-side">
          <div className="card">
            <div className="pkg-head">
              <span className="pkg-head-icon">
                <Eye size={22} />
              </span>
              <span>
                <span className="pkg-head-title">Live preview</span>
                <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                  How the campaign looks on the TV.
                </span>
              </span>
            </div>
            <div style={{ marginTop: 14 }}>
              <CampaignVisual m={m} img={img} />
            </div>
          </div>
          <div className="card">
            <div className="pkg-head">
              <span className="pkg-head-icon">
                <ClipboardList size={22} />
              </span>
              <span>
                <span className="pkg-head-title">Campaign summary</span>
                <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                  The settings at a glance.
                </span>
              </span>
            </div>
            <div className="fac-detail-list" style={{ borderTop: 0 }}>
              <div className="fac-detail-row">
                <Tag size={16} />
                <span>Type</span>
                <span className="fac-detail-val">{m.type ? TYPE_LABEL[m.type] : '—'}</span>
              </div>
              <div className="fac-detail-row">
                <Monitor size={16} />
                <span>Display</span>
                <span className="fac-detail-val">{m.format ? FORMAT_LABEL[m.format] : '—'}</span>
              </div>
              <div className="fac-detail-row">
                <Users size={16} />
                <span>Audience</span>
                <span className="fac-detail-val">{audienceText(aud)}</span>
              </div>
              <div className="fac-detail-row" style={{ alignItems: 'flex-start' }}>
                <CalendarClock size={16} />
                <span>Schedule</span>
                <span className="fac-detail-val">
                  {schedule || '—'}
                  <span className="muted" style={{ display: 'block', fontWeight: 500, fontSize: 12 }}>
                    {m.frequency ? FREQ_LABEL[m.frequency] : ''}
                  </span>
                </span>
              </div>
              <div className="fac-detail-row">
                <CheckCircle2 size={16} />
                <span>Status</span>
                <span className="fac-detail-val">
                  <span className={`state-pill ${status.cls}`}>
                    <span className="state-dot" /> {status.label}
                  </span>
                </span>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="pkg-head">
              <span className="pkg-head-icon">
                <Lightbulb size={22} />
              </span>
              <span>
                <span className="pkg-head-title">Tips</span>
                <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                  Make campaigns easy to read from the bed.
                </span>
              </span>
            </div>
            <div className="camp-tips">
              {[
                ['Use a short, clear title', 'A few words that read well from across the room.'],
                ['Add a landscape photo', '16:9 works best, e.g. 1920×1080.'],
                ['Keep the text short', 'The key benefit and what to do next.'],
                ['Pick the audience and the timing', 'The right guests at the right moment, e.g. happy hour in the evening.'],
              ].map(([t, s]) => (
                <div key={t}>
                  <CheckCircle2 size={18} />
                  <span>
                    <strong>{t}</strong>
                    <span className="muted">{s}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
